import React, { useState } from 'react';
import { Modal, SparkModalPortal, Spinner } from '@spark';
import CostEstimateFooter from '@/features/billing/components/CostEstimateFooter';
import { TrainFromSpecificUsecase } from '@/features/trainings/components/TrainFromSpecificUsecase';
import ReviewDataOverview from './ReviewDataOverview';
import {
    FULL_TRAINING_OVERVIEW_TITLE,
    REVIEW_PLAN_TITLE,
    START_TRAINING_LABEL,
} from './reviewOverview';

/**
 * Review Training Plan: data overview plus the existing summary-modal train
 * surface. Cancel stays on Model Setup. Start Training runs the same train
 * action the Train step used. A low label count warns; it does not disable
 * Start Training. Existing train blockers still do.
 *
 * Two exits finish the wizard through `onFinished`: a training that actually
 * started, and a scheduled auto-train once its "Training will start when
 * data is ready" modal is dismissed (`onFinished({ trainingScheduled: true })`).
 */
const ReviewTrainingPlanModal = ({
    open,
    hidden = false,
    usecase,
    refreshKey = 0,
    moveToScreen,
    onClose,
    onFinished,
    onOpenDataEditor,
}) => {
    const [trainAction, setTrainAction] = useState(null);
    // Slot above Data Overview that TrainFromSpecificUsecase portals its
    // data-flows / auto-train banner into (held in state so the portal
    // target exists before it renders).
    const [bannerSlot, setBannerSlot] = useState(null);

    const handleTrainingStarted = () => {
        onFinished?.();
    };
    const handleAutoTrainScheduled = () => {
        onFinished?.({ trainingScheduled: true });
    };

    return (
        <SparkModalPortal
            open={open}
            hidden={hidden}
            onClose={onClose}
            size="wide"
            destroyOnClose
            className="cw-review-plan-portal"
        >
            <Modal
                title={REVIEW_PLAN_TITLE}
                showSubtitle={false}
                className="cw-review-plan-modal"
                primaryLabel={trainAction?.loading ? 'Training…' : START_TRAINING_LABEL}
                primaryIcon={trainAction?.loading ? <Spinner size="small" /> : undefined}
                primaryDisabled={!trainAction || !!trainAction.disabled || !!trainAction.loading}
                primaryDisableWhenReadOnly
                tertiaryLabel="Cancel"
                showFooterSlot={Boolean(trainAction?.usecaseId)}
                footerSlot={
                    trainAction?.usecaseId ? (
                        <CostEstimateFooter
                            operation="train"
                            label="model training"
                            params={{ usecase_id: trainAction.usecaseId }}
                            refreshKey={refreshKey}
                        />
                    ) : null
                }
                onPrimaryClick={() => {
                    void trainAction?.onClick?.();
                }}
                onTertiaryClick={onClose}
                onCloseClick={onClose}
            >
                {open ? (
                    <div className="cw-review-plan">
                        <div ref={setBannerSlot} className="cw-review-plan__banner" />
                        <ReviewDataOverview
                            usecase={usecase}
                            refreshKey={refreshKey}
                            onReviewExamples={() => onOpenDataEditor?.()}
                        />
                        <TrainFromSpecificUsecase
                            layout="summary-modal"
                            embeddedInTaskDrawer
                            summaryTitle={FULL_TRAINING_OVERVIEW_TITLE}
                            autoTrainSectionContainer={bannerSlot}
                            moveToScreen={moveToScreen}
                            onTrainActionChange={setTrainAction}
                            onTrainingStarted={handleTrainingStarted}
                            onAutoTrainScheduled={handleAutoTrainScheduled}
                            onNavigateAway={onClose}
                            onOpenDataEditor={onOpenDataEditor}
                        />
                    </div>
                ) : null}
            </Modal>
        </SparkModalPortal>
    );
};

export default ReviewTrainingPlanModal;
